import config from '@/config';
import { ApiBase, InternationalizedField, ListOptions } from '@/api/common';
import ApiList from '@/api/list';
import { Mutation } from '@/store';
import store from '@/store';
import { bus } from '@/bus';
import { BusEvent } from '@/models/bus';

const NO_UPLOAD_FIELDS = ['SLUG', 'created', 'updated', 'bg_image_link'];

export default class Category extends ApiBase<Category> {
  public static readonly SLUG: string = 'category';
  public readonly SLUG: string = 'category';
  private static readonly URL_BASE: string = config.API_ROOT + '/category/';

  // Initializing these fields (even with undefined)
  // is necessary, so hasOwnAttribute can find them.
  public school?: number = undefined;
  public order?: number = undefined;
  public name: InternationalizedField<string> = new InternationalizedField<string>();
  public created?: string = undefined;
  public updated?: string = undefined;
  public bg_image?: number | null = undefined;
  public bg_image_link?: string | null = undefined;
  public parent_project?: any = undefined;

  public constructor() {
    super({
      urlBase: Category.URL_BASE,
      filteredFields: NO_UPLOAD_FIELDS
    });
  }

  public assign(response: any): Category {
    super.assign(response);

    if (response) {
      if ('name' in response) {
        this.name = new InternationalizedField<string>(response.name);
      }
    }

    return this;
  }

  public static async get(id: number): Promise<Category> {
    return this.doGet({
      urlBase: Category.URL_BASE,
      id,
      create: (response) => new Category().assign(response)
    });
  }

  public static async list(): Promise<ApiList<Category>> {
    const options: ListOptions<Category> = {
      urlBase: Category.URL_BASE,
      create: (response) => new Category().assign(response)
    };
    return this.doList(options);
  }

  public static async listAll(): Promise<ApiList<Category>> {
    return this.doList({
      urlBase: Category.URL_BASE,
      create: (response) => new Category().assign(response),
      all: true,
      parameters: {
        minimal: true
      },
      readFromCache: () => store.state.categoryList,
      writeToCache: async (list: ApiList<Category>) =>
        await store.commit(Mutation.CACHE_CATEGORY_LIST, list)
    });
  }

  public async delete(): Promise<void> {
    const id = this.id;
    await super.delete();

    // In case the entity is cached
    await store.commit(Mutation.DELETE_FROM_CATEGORY_LIST, id);
    bus.$emit(BusEvent.CACHE_CHANGED);
  }

  public async save(): Promise<number> {
    const isNew = !this.id;
    const id = await super.save();

    if (isNew) {
      await store.commit(Mutation.ADD_TO_CATEGORY_LIST, this);
    } else {
      await store.commit(Mutation.CATEGORY_UPDATED, this);
    }

    bus.$emit(BusEvent.CACHE_CHANGED);

    return id;
  }

  public getName(): string {
    const name = this.name.getDefaultValue();
    if (name) {
      return name;
    }
    return '';
  }

  public getFormattedDate(): string {
    if (this.updated === undefined) {
      return 'N/A';
    }
    const index = this.updated.toLocaleString().indexOf('.');
    return this.updated.toLocaleString().substring(0, index);
  }
}
